import { useState, useEffect, useCallback } from 'react'
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { BookingStatus } from '@/types/booking'

/**
 * Hook to fetch booking statuses for multiple chats
 * Returns a map of chatId -> bookingStatus (only for active/non-completed bookings)
 * If a chat has multiple bookings, returns the most recent active one
 */
export function useBookingsForChats(chatIds: string[]) {
  const [bookingStatuses, setBookingStatuses] = useState<Record<string, BookingStatus>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!chatIds || chatIds.length === 0) {
      setBookingStatuses({})
      setLoading(false)
      return
    }

    const fetchBookingStatuses = async () => {
      try {
        setLoading(true)
        const bookingsRef = collection(db, 'bookings')
        
        // Query bookings for all these chat IDs
        const bookingQuery = query(
          bookingsRef,
          where('chatId', 'in', chatIds)
        )
        
        const querySnapshot = await getDocs(bookingQuery)
        const statuses: Record<string, BookingStatus> = {}
        
        // Group bookings by chatId
        const bookingsByChat: Record<string, any[]> = {}
        querySnapshot.forEach((doc) => {
          const data = doc.data()
          const chatId = data.chatId as string
          if (!bookingsByChat[chatId]) {
            bookingsByChat[chatId] = []
          }
          bookingsByChat[chatId].push(data)
        })
        
        // For each chat, find the most recent active booking
        Object.entries(bookingsByChat).forEach(([chatId, bookings]) => {
          // Sort by createdAt descending (most recent first)
          const sortedBookings = bookings.sort((a, b) => {
            const aTime = (a.createdAt as Timestamp)?.toMillis?.() || 0
            const bTime = (b.createdAt as Timestamp)?.toMillis?.() || 0
            return bTime - aTime
          })
          
          // Find the most recent active booking (not completed, no_show, or cancelled)
          const activeBooking = sortedBookings.find(b => 
            b.status !== 'completed' && b.status !== 'no_show' && b.status !== 'cancelled'
          )
          
          // If we found an active booking, use it; otherwise use the most recent one
          const bookingToUse = activeBooking || sortedBookings[0]
          if (bookingToUse) {
            statuses[chatId] = bookingToUse.status as BookingStatus
          }
        })
        
        setBookingStatuses(statuses)
        setError(null)
      } catch (err) {
        // Permission errors are expected for some users
        if (err instanceof Error && err.message.includes('permission')) {
          console.log('[useBookingsForChats] User does not have permission to query bookings')
          setBookingStatuses({})
        } else {
          console.error('[useBookingsForChats] Error fetching booking statuses:', err)
          setError(err instanceof Error ? err.message : 'Failed to fetch booking statuses')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchBookingStatuses()
  }, [chatIds.join(','), refreshKey]) // Include refreshKey to allow manual refresh

  // Function to manually trigger a refetch
  const refetch = useCallback(() => {
    setRefreshKey(prev => prev + 1)
  }, [])

  return { bookingStatuses, loading, error, refetch }
}
